import { Bot, Integration } from 'botpress-client'
import ReactMarkdown from 'react-markdown'
import { markdownComponents } from '~/components'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import cx from 'classnames'
import { isEqual } from 'lodash'
import { ComponentProps, useEffect, useState } from 'react'
import { Card, Spinner, Switch, Tooltip } from '../../../elements'
import { Button } from '~/elementsv2'
import { useFeature, useReadFile, useSetPreferences, useUpdateBot, useWebchatV2 } from '../../../hooks'
import { showSuccessToast } from '../../../services'
import { IntegrationIcon } from '.'
import { ConfigurationForm } from './ConfigurationForm'
import type { Permission } from '../../users/types'
import * as Collapsible from '@radix-ui/react-collapsible'
import { isBotpressWorkspace } from '~/utils'
import { PublicIntegration } from '../types'
import { Link } from '@radix-ui/themes'
import { useSuspenseQuery } from '@tanstack/react-query'
import { userPreferencesQueryOptions } from '~/queries'
import { useAsync } from 'react-use'
import { WebchatCurrentRevision } from '~/services/preferences'

type IntegrationFormProps = {
  disabled?: boolean
  integration: Integration
  botIntegration: Bot['integrations'][string]
  workspaceId: string
  botId: string
  authorize: (permission: Permission) => boolean | undefined
} & ComponentProps<'div'>

export function IntegrationForm({
  integration,
  workspaceId,
  botId,
  botIntegration,
  authorize,
  ...props
}: IntegrationFormProps) {
  const userCanConfigure = authorize('bots.update')
  const [integrationEnabled, setIntegrationEnabled] = useState<boolean>(botIntegration?.enabled ?? false)
  const [formErrors, setFormErrors] = useState<Record<string, any>[] | undefined>([])
  const [edited, setEdited] = useState<boolean>(false)

  const [integrationConfig, setIntegrationConfig] = useState<Record<string, any>>(botIntegration?.configuration ?? {})

  useEffect(() => {
    setIntegrationEnabled(botIntegration?.enabled ?? false)
    setFormErrors([])
    setEdited(false)
    setIntegrationConfig(botIntegration?.configuration ?? {})
  }, [botIntegration])

  const onChange = (data: Record<string, any>) => {
    setEdited(!isEqual(data, botIntegration?.configuration ?? {}))
    setIntegrationConfig(data)
  }

  const webhookUrl = botIntegration?.webhookUrl
  const webhookId = botIntegration?.webhookId
  const identifier = botIntegration?.identifier

  const identifierRequired = integration.configuration.identifier.required
  const linkTemplateScript = integration.configuration.identifier.linkTemplateScript
  const integrationStatus = botIntegration?.enabled && botIntegration?.status
  const integrationStatusMessage = botIntegration?.statusReason

  const newSlackEnabled = useFeature('slackNewForm')
  const usingNewIntegrationForm = integration.name === 'slack' && newSlackEnabled // Update this as new integration forms are added behind feature toggles.
  const showEnableIntegrationToggle = !usingNewIntegrationForm || !identifierRequired || identifier

  const { mutate: updateBot, isPending, mutateAsync: updateBotAsync } = useUpdateBot()

  const webchatV2Revision = useSuspenseQuery(
    userPreferencesQueryOptions({ path: '$workspaceId/$botId/webchatV2Revision', params: { botId, workspaceId } })
  ).data
  const { mutate: setPreferences } = useSetPreferences()
  const { isWebchatV2Enabled } = useWebchatV2()

  const updateBotIntegration = (configuration: Record<string, any>) => {
    updateBot({
      id: botId,
      workspaceId,
      integrations: {
        [integration.id]: { enabled: integrationEnabled, configuration },
      },
      onSuccess: () => {
        setEdited(false)
        showSuccessToast('The integration configuration has been updated.')
      },
    })
  }

  useAsync(async () => {
    if (webchatV2Revision < WebchatCurrentRevision && isWebchatV2Enabled && integration.name === 'webchat') {
      await updateBotAsync({
        id: botId,
        workspaceId,
        integrations: {
          [integration.id]: { enabled: integrationEnabled, configuration: integrationConfig },
        },
      })
      setPreferences({
        path: '$workspaceId/$botId/webchatV2Revision',
        value: WebchatCurrentRevision,
        params: { botId, workspaceId },
      })
    }
  }, [webchatV2Revision, isWebchatV2Enabled, integration])

  if (!botId || !workspaceId || !integration) {
    return (
      <div className="flex h-44 w-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className={cx('mb-16 flex flex-col gap-6', props.className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Card className="mr-4 flex-none">
            <IntegrationIcon className="h-8 w-8" iconUrl={integration?.iconUrl} />
          </Card>
          <div className="flex flex-col ">
            <div className="flex items-center">
              <div>
                <h2 className="mr-1 text-2xl font-medium">{integration.title}</h2>
                <p className="text-sm italic text-gray-10">v{integration.version}</p>
              </div>
              {integrationStatus === 'registration_failed' && (
                <Tooltip
                  label={
                    integrationStatusMessage
                      ? `The integration configuration failed: ${integrationStatusMessage}`
                      : 'The integration configuration failed. Please review it and try again.'
                  }
                >
                  <ExclamationCircleIcon className="h-6 text-red-9" />
                </Tooltip>
              )}
            </div>
            <div className="flex max-w-sm">
              <Tooltip label={identifier} disabled={!identifier} className="text-sm text-gray-10 ellipsis">
                {identifier ?? ''}
              </Tooltip>
            </div>
          </div>
        </div>
        {showEnableIntegrationToggle ? (
          <Switch
            className="ml-auto flex-none"
            label="Enable integration"
            checked={integrationEnabled ?? false}
            onCheckedChange={(checked) => {
              setEdited(true)
              setIntegrationEnabled(checked)
            }}
          />
        ) : (
          <div />
        )}
      </div>
      <div>
        <ConfigurationForm
          webhookUrl={webhookUrl}
          webhookId={webhookId}
          linkTemplateScript={linkTemplateScript}
          readonly={isPending || !userCanConfigure}
          setErrors={setFormErrors}
          setData={onChange}
          data={integrationConfig}
          iconUrl={integration.iconUrl}
          schema={integration.configuration.schema}
          identifierRequired={identifierRequired}
          identifier={identifier}
          workspaceId={workspaceId}
          botId={botId}
          integration={integration}
        />
      </div>
      <div className="flex justify-between">
        <IntegrationReadmeCollapsible integration={integration} />
        <Button
          onClick={() => {
            updateBotIntegration(integrationConfig)
          }}
          disabled={!formErrors || formErrors.length !== 0 || isPending || !edited}
        >
          Save configuration
        </Button>
      </div>
    </div>
  )
}

function isPublicIntegration(integration: Integration): integration is PublicIntegration {
  return integration.public
}

const IntegrationReadmeCollapsible = ({ integration }: { integration: Integration }) => {
  const { data: readme } = useReadFile({
    url: integration?.readmeUrl,
    errorHandler: (err) => {
      // Note: readme files are stored in S3 which returns an HTTP 403 code if the file doesn't exist
      if (err.response?.status === 403) {
        console.warn(`Readme file for integration "${integration.name}" doesn't exist`)
      } else {
        throw err
      }
    },
  })
  const isBpWorkspace = isPublicIntegration(integration) && isBotpressWorkspace(integration.ownerWorkspace.id)

  //We assume that our Integrations are documented on the documentation website even though it's not always the case
  if (isBpWorkspace) {
    return (
      <Link
        href={
          integration.name === 'webchat'
            ? 'https://botpress.com/docs/developers/webchat-v2/quick-start/'
            : `https://botpress.com/docs/cloud/channels/${integration.name}`
        }
        target="_blank"
        rel="noopener noreferrer"
      >
        <Button variant="ghost">Show instructions</Button>
      </Link>
    )
  }

  return (
    <Collapsible.Root className="CollapsibleRoot">
      <Collapsible.Trigger asChild>
        <Button variant="ghost">Show instructions</Button>
      </Collapsible.Trigger>

      <Collapsible.Content>
        <div className="px-2 py-6">
          <ReactMarkdown children={readme} components={markdownComponents} />
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  )
}
